// @flow

import { createSelector } from 'reselect';

import {
  selectCurrency as selectPrepaidCurrency,
  selectAvailableCurrencies as selectBUCurrencies,
} from '@gandi/billing';
import type { Currency } from '@gandi/billing';
import {
  selectCartOwnerList,
  selectCartUUID,
  type PartialOrganization,
} from '@gandi/cart';
import {
  selectOneOrganization,
  selectOrganizationsList,
  type Organization,
} from '@gandi/redux-organization';
import { selectCurrencies } from '@gandi/redux-places';
import { selectCurrentId } from '@gandi/redux-sharing';

import { selectShopCurrent } from '../../../../modules/organization/selectors';
import type { FullState } from '../../../../types';

import { FETCH_ORGANIZATIONS_PARAMS } from './index';

const EMPTY_ARRAY: Array<string> = [];

type SelectShopCurrenciesArgs = { sharingId?: string, country: string };
export const selectShopCurrencies: (
  state: FullState,
  args: SelectShopCurrenciesArgs,
) => Array<string> | Array<Currency> = createSelector(
  [
    state => state,
    (state, args) => args.sharingId,
    (state, { country }: SelectShopCurrenciesArgs) => country,
  ],
  (state, sharingId, country) => {
    if (sharingId) {
      // use the prepaid if set
      // usual authenticated users
      const prepaidCurrency = selectPrepaidCurrency(state, { sharingId });
      if (prepaidCurrency) {
        return [prepaidCurrency];
      }

      // fallback on all BU currencies
      // if the orga never did a payment before
      const BUCurrencies = selectBUCurrencies(state, { sharingId, country });
      if (BUCurrencies && BUCurrencies.length > 0) {
        return BUCurrencies;
      }
    }

    // fallback to geo guess currencies
    // anonymous users are in this case

    // todo make this selectCurrencies return Array<Currency>
    const countryCurrencies = selectCurrencies(state, country);
    return countryCurrencies || EMPTY_ARRAY;
  },
);

export const selectOrgCountry = (state: FullState, orgId: string): string => {
  const currentId = selectCurrentId(state);
  const org =
    orgId === currentId
      ? selectShopCurrent(state, false)
      : selectOneOrganization(state, orgId);

  return org?.country || '';
};

export const selectOrgBU = (state: FullState, orgId: string): number | void => {
  const currentId = selectCurrentId(state);
  const org =
    orgId === currentId
      ? selectShopCurrent(state, false)
      : selectOneOrganization(state, orgId);

  return org ? org.bu_id : undefined;
};

export const selectShopOrgaList = (
  state: FullState,
): Array<Organization> | Array<PartialOrganization> => {
  const cartId = selectCartUUID(state);
  const organizationsList = selectOrganizationsList(
    state,
    FETCH_ORGANIZATIONS_PARAMS,
  );
  const cartOwnerList = selectCartOwnerList(state);

  return cartId ? cartOwnerList : organizationsList;
};
